/**
 * Turning a priced cost table into something the operator can send RIGHT NOW.
 *
 * The operator's actual words were "just send a screenshot to the customer".
 * Until now that meant an OS screenshot, because FreightKit had no image export
 * anywhere and no quotation PDF template — the only email-safe output required
 * a finalised, persisted quote first.
 *
 * Both paths here are fully client-side: no backend, no persistence, no
 * finalise step. html2canvas and jsPDF are already bundled and proven in
 * GenerateDocuments.jsx.
 *
 * The renderer and PDF constructor are injected so this logic is testable —
 * jsdom has no real canvas.
 */

/* html2canvas and jsPDF are loaded on demand rather than imported at module
   scope. Two reasons: they are large and only needed the moment an operator
   actually exports, so they stay out of the initial bundle; and importing jsPDF
   eagerly drags in a transitive dependency that needs TextEncoder, which this
   jsdom test environment does not provide — so an eager import makes the whole
   module untestable. */
const loadHtml2Canvas = async () => (await import("html2canvas")).default;
const loadJsPdf = async () => (await import("jspdf")).default;

const CAPTURE_DEFAULTS = {
  scale: 2, // legible when pasted into an email at 100%
  useCORS: true,
  backgroundColor: "#ffffff",
  logging: false,
};

async function capture(element, renderCanvas) {
  if (!element) {
    return { canvas: null, reason: "There is nothing to export yet." };
  }
  try {
    const render = renderCanvas || (await loadHtml2Canvas());
    const canvas = await render(element, CAPTURE_DEFAULTS);
    return { canvas, reason: null };
  } catch (err) {
    return { canvas: null, reason: err?.message || String(err) };
  }
}

/**
 * Put the table on the clipboard as a PNG, ready to paste straight into an
 * email or a WhatsApp thread.
 */
export async function copyElementAsPng(
  element,
  {
    renderCanvas,
    clipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined,
    ClipboardItemCtor = typeof window !== "undefined" ? window.ClipboardItem : undefined,
  } = {}
) {
  const { canvas, reason } = await capture(element, renderCanvas);
  if (!canvas) return { ok: false, reason };

  // Firefox ships no ClipboardItem. Say so plainly and point at the PDF, rather
  // than failing silently and leaving the operator clicking a dead button.
  if (!ClipboardItemCtor || !clipboard?.write) {
    return {
      ok: false,
      reason:
        "This browser cannot copy images to the clipboard — use Download PDF instead.",
    };
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    return { ok: false, reason: "Could not turn the table into an image." };
  }

  try {
    await clipboard.write([new ClipboardItemCtor({ "image/png": blob })]);
    return { ok: true, reason: null };
  } catch (err) {
    return { ok: false, reason: err?.message || String(err) };
  }
}

/**
 * Download the table as an A4 PDF, paginating a tall table across pages rather
 * than squashing it into one.
 */
export async function downloadElementAsPdf(
  element,
  filename = "quotation.pdf",
  { renderCanvas, PdfCtor } = {}
) {
  const { canvas, reason } = await capture(element, renderCanvas);
  if (!canvas) return { ok: false, reason };

  try {
    const Ctor = PdfCtor || (await loadJsPdf());
    const pdf = new Ctor("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // JPEG rather than PNG: a screenshot-sized PNG of a wide table routinely
    // lands at several MB, which bounces off mailbox attachment limits.
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
    return { ok: true, reason: null };
  } catch (err) {
    return { ok: false, reason: err?.message || String(err) };
  }
}
